import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  CalendarDays,
  FileText,
  TrendingUp,
  Clock,
  AlertTriangle,
  MessageSquare,
  FilePen,
  Activity,
  ChevronRight,
  Calendar,
  Download,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import api, { invoicesApi, demoApi, chatApi } from '../services/api';
import { useTour } from '../hooks/useTour';
import { dashboardTourSteps } from '../tours/dashboardTour';
import { SkeletonDashboard } from '../components/Skeleton';
import OnboardingHelpBanner from '../components/HelpCenter/OnboardingHelpBanner';
import './Dashboard.css';

// --- Types ---

interface TrendData {
  clients_prev: number;
  sessions_prev: number;
  revenue_prev: number;
}

interface ChartSummary {
  total_revenue: number;
  total_sessions: number;
  revenue_change_pct: number | null;
  sessions_change_pct: number | null;
  avg_revenue_monthly: number;
  avg_sessions_monthly: number;
}

interface UpcomingSession {
  id: string;
  client_id: string;
  client_name: string;
  case_title: string;
  scheduled_at: string;
  session_type: string;
  payment_status: string;
  duration_minutes: number | null;
  location_type: string | null;
}

interface OverdueInvoice {
  id: string;
  invoice_number: string;
  client_name: string;
  total_eur: number;
  due_date: string;
}

interface OpenTasks {
  overdue_invoices_count: number;
  overdue_invoices_total_eur: number;
  unread_followup_responses: number;
  pending_contracts: number;
}

interface RecentActivity {
  id: string;
  entity_type: string;
  action: string;
  description: string | null;
  timestamp: string;
  entity_id: string | null;
}

interface DashboardData {
  active_clients: number;
  sessions_this_month: number;
  open_invoices_count: number;
  open_invoices_total_eur: number;
  monthly_revenue_eur: number;
  upcoming_sessions: UpcomingSession[];
  overdue_invoices: OverdueInvoice[];
  open_tasks: OpenTasks;
  recent_activities: RecentActivity[];
  trend: TrendData;
}

interface DashConversation {
  id: string;
  client_id: string;
  client_name: string | null;
  preferred_channel: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  unread_count: number;
}

interface ChartMonth {
  month: string;
  revenue: number;
  sessions: number;
}

interface ChartData {
  months: ChartMonth[];
  summary: ChartSummary;
}

// --- Helpers ---

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Guten Morgen';
  if (h < 18) return 'Guten Tag';
  return 'Guten Abend';
}

function formatEur(value: number): string {
  return value.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' \u20AC';
}

function formatEurShort(value: number): string {
  return value.toLocaleString('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }) + ' \u20AC';
}

function relativeTime(isoString: string): string {
  const now = Date.now();
  const ts = new Date(isoString).getTime();
  const diffMs = now - ts;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'gerade eben';
  if (diffMin < 60) return `vor ${diffMin} Min.`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `vor ${diffH} Std.`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return 'gestern';
  return `vor ${diffD} Tagen`;
}

function formatSessionTime(isoString: string): string {
  const d = new Date(isoString);
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) + ' Uhr';
}

function formatDay(isoString: string): { day: string; month: string } {
  const d = new Date(isoString);
  return {
    day: d.getDate().toString(),
    month: d.toLocaleDateString('de-DE', { month: 'short' }).replace('.', ''),
  };
}

function formatChartMonth(monthStr: string): string {
  const [, m] = monthStr.split('-');
  const months = ['Jan', 'Feb', 'M\u00E4r', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  return months[parseInt(m, 10) - 1] || m;
}

function isToday(isoString: string): boolean {
  const d = new Date(isoString);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
}

function formatLocationLabel(loc: string | null): string {
  if (!loc) return '';
  const map: Record<string, string> = {
    online: 'Online',
    in_person: 'Vor Ort',
    phone: 'Telefon',
    hybrid: 'Hybrid',
    vor_ort: 'Vor Ort',
    telefon: 'Telefon',
  };
  return map[loc] || loc;
}

function formatSessionTypeLabel(t: string): string {
  const map: Record<string, string> = {
    individual: 'Einzelcoaching',
    couple: 'Paarcoaching',
    family: 'Familiencoaching',
    group: 'Gruppencoaching',
    fuehrungskraefte: 'F\u00FChrungskr\u00E4fte',
    executive: 'Executive',
  };
  return map[t] || t;
}

function getActivityRoute(a: RecentActivity): string | null {
  if (!a.entity_id) return null;
  switch (a.entity_type) {
    case 'client': return `/klienten/${a.entity_id}`;
    case 'invoice': return '/rechnungen';
    case 'session': return '/kalender';
    case 'case': return '/klienten';
    case 'followup': return '/klienten';
    default: return null;
  }
}

function getActivityDotClass(entityType: string): string {
  switch (entityType) {
    case 'invoice': return 'activity-dot-amber';
    case 'session': return 'activity-dot-blue';
    case 'client': return 'activity-dot-green';
    case 'case': return 'activity-dot-purple';
    default: return '';
  }
}

function formatDateHeader(): string {
  return new Date().toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

// --- Messaging Helpers ---

const MSG_AVATAR_COLORS = ['#6366F1', '#0D9488', '#EA580C', '#3B82F6', '#8B5CF6', '#EC4899'];

function getMsgAvatarColor(name: string | null): string {
  if (!name) return MSG_AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return MSG_AVATAR_COLORS[Math.abs(hash) % MSG_AVATAR_COLORS.length];
}

function getMsgInitials(name: string | null): string {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

function formatMsgTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  if (diff < 86400000 && d.getDate() === now.getDate()) {
    return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }
  if (diff < 604800000) {
    return d.toLocaleDateString('de-DE', { weekday: 'short' });
  }
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

// --- Quick Actions ---

const QUICK_ACTIONS = [
  { label: 'Neue Sitzung', icon: Plus, to: '/sitzung-anlegen' },
  { label: 'Rechnung erstellen', icon: FileText, to: '/rechnungen?neu=1' },
  { label: 'Klient anlegen', icon: Users, to: '/klienten?neu=1' },
  { label: 'Termin planen', icon: Calendar, to: '/kalender' },
];

// --- Custom Chart Tooltip ---

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const revenue = payload.find((p: any) => p.dataKey === 'revenue');
  const sessions = payload.find((p: any) => p.dataKey === 'sessions');
  return (
    <div className="chart-custom-tooltip">
      <div className="chart-tooltip-label">{formatChartMonth(String(label))}</div>
      {revenue && (
        <div className="chart-tooltip-row">
          <span className="chart-tooltip-dot" style={{ background: CHART_GREEN }} />
          <span>Umsatz:</span>
          <strong>{formatEur(revenue.value)}</strong>
        </div>
      )}
      {sessions && (
        <div className="chart-tooltip-row">
          <span className="chart-tooltip-dot" style={{ background: CHART_BLUE }} />
          <span>Sitzungen:</span>
          <strong>{sessions.value}</strong>
        </div>
      )}
    </div>
  );
}

// --- Trial Banner ---

function DemoBanner() {
  const { user, refreshUser } = useAuth();
  const [clearing, setClearing] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const handleClear = async () => {
    if (!window.confirm('Alle Demo-Daten (Beispiel-Klienten, Fälle, Rechnungen) werden unwiderruflich gelöscht. Fortfahren?')) return;
    setClearing(true);
    try {
      await demoApi.clear();
      await refreshUser?.();
      window.location.reload();
    } catch { setClearing(false); }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await demoApi.seed();
      await refreshUser?.();
      window.location.reload();
    } catch { setSeeding(false); }
  };

  if (user?.demo_data_seeded) {
    return (
      <div className="card mb-16" style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 20px', background: 'var(--info-bg, var(--bg))',
        border: '1px solid var(--info, var(--border))',
      }}>
        <Activity size={18} style={{ color: 'var(--info, var(--primary))', flexShrink: 0 }} />
        <span style={{ flex: 1, fontSize: '0.88rem', color: 'var(--text)' }}>
          <strong>Demo-Modus:</strong> Sie sehen Beispieldaten zum Ausprobieren.
        </span>
        <button
          className="btn btn-sm btn-secondary"
          onClick={handleClear}
          disabled={clearing}
          style={{ whiteSpace: 'nowrap' }}
        >
          {clearing ? 'Wird gelöscht...' : 'Demo-Daten löschen'}
        </button>
      </div>
    );
  }

  return (
    <div className="card mb-16" style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 20px', background: 'var(--bg)',
      border: '1px dashed var(--border)',
    }}>
      <Activity size={18} style={{ color: 'var(--primary)', flexShrink: 0 }} />
      <span style={{ flex: 1, fontSize: '0.88rem', color: 'var(--text)' }}>
        <strong>Tipp:</strong> Lade Beispieldaten, um die Plattform mit Klienten, Fällen und Rechnungen auszuprobieren.
      </span>
      <button
        className="btn btn-sm btn-primary"
        onClick={handleSeed}
        disabled={seeding}
        style={{ whiteSpace: 'nowrap' }}
      >
        {seeding ? 'Wird geladen...' : 'Demo-Daten laden'}
      </button>
    </div>
  );
}

function TrialBanner() {
  const { subscription } = useAuth();
  const navigate = useNavigate();

  if (!subscription || subscription.status !== 'trial') return null;
  const days = subscription.trial_days_remaining;
  if (days == null || days > 7) return null;

  const isUrgent = days <= 3;

  return (
    <div
      className={`card mb-16 trial-banner ${isUrgent ? 'trial-banner-urgent' : 'trial-banner-warning'}`}
      onClick={() => navigate('/mein-konto')}
    >
      <Clock size={20} style={{ color: isUrgent ? 'var(--danger)' : 'var(--warning)' }} />
      <div style={{ flex: 1 }}>
        <strong style={{ color: isUrgent ? 'var(--danger)' : 'var(--warning)' }}>
          {days > 0
            ? `Ihr Free Trial endet in ${days} Tag${days !== 1 ? 'en' : ''}`
            : 'Ihr Free Trial ist abgelaufen'}
        </strong>
        <span className="text-muted text-sm" style={{ marginLeft: 8 }}>
          Wählen Sie einen Plan, um weiterzuarbeiten.
        </span>
      </div>
      <button
        className="btn btn-sm btn-primary"
        onClick={(e) => { e.stopPropagation(); navigate('/mein-konto'); }}
      >
        Pläne vergleichen
      </button>
    </div>
  );
}

// --- Trend Badge ---

function TrendBadge({ current, previous, isPercent }: { current: number; previous: number; isPercent?: boolean }) {
  if (previous === 0 && current === 0) return null;
  const diff = current - previous;
  if (diff === 0) return null;

  const isUp = diff > 0;
  let label: string;
  if (isPercent && previous > 0) {
    const pct = Math.round((diff / previous) * 100);
    label = `${isUp ? '+' : ''}${pct}%`;
  } else {
    label = `${isUp ? '+' : ''}${diff}`;
  }

  return (
    <span className={`trend-badge ${isUp ? 'trend-up' : 'trend-down'}`}>
      {isUp ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
      {label}
    </span>
  );
}

// --- Change Percent Badge ---

function ChangeBadge({ pct }: { pct: number | null }) {
  if (pct == null) return null;
  const isUp = pct >= 0;
  return (
    <span className={`change-badge ${isUp ? 'change-up' : 'change-down'}`}>
      {isUp ? '\u25B2' : '\u25BC'} {isUp ? '+' : ''}{pct}%
    </span>
  );
}

// --- Chart Y-axis formatter ---

function formatYAxis(v: number): string {
  if (v >= 1000) return `${(v / 1000).toLocaleString('de-DE')}k`;
  return String(v);
}

// Chart colors — fixed vivid colors matching mockup (CSS vars too muted for charts)
const CHART_GREEN = '#22c55e';
const CHART_BLUE = '#3b82f6';

// --- Component ---

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useTour('dashboard', dashboardTourSteps, { delay: 1200 });
  const [data, setData] = useState<DashboardData | null>(null);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [chartRange, setChartRange] = useState<number>(6);
  const [recentConvs, setRecentConvs] = useState<DashConversation[]>([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [error, setError] = useState(false);

  const loadStats = useCallback(() => {
    setError(false);
    const controller = new AbortController();
    api.get('/dashboard/stats', { signal: controller.signal })
      .then((res) => setData(res.data))
      .catch((err) => {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        setError(true);
      });
    return controller;
  }, []);

  useEffect(() => {
    const controller = loadStats();
    // Load recent conversations for messaging widget
    chatApi.conversations({ limit: 5 })
      .then((res) => {
        const items: DashConversation[] = res.data.items || [];
        setRecentConvs(items);
        setTotalUnread(items.reduce((sum: number, c: DashConversation) => sum + (c.unread_count || 0), 0));
      })
      .catch(() => {});
    return () => controller.abort();
  }, [loadStats]);

  useEffect(() => {
    const controller = new AbortController();
    api.get('/dashboard/chart-data', {
      params: chartRange ? { months: chartRange } : undefined,
      signal: controller.signal,
    })
      .then((res) => setChartData(res.data))
      .catch((err) => {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        // Chart error is non-critical; don't set global error state
      });
    return () => controller.abort();
  }, [chartRange]);

  const handleDownloadPdf = useCallback(async (activityId: string, entityType: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (entityType !== 'invoice') return;
    try {
      const res = await invoicesApi.downloadPdf(activityId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rechnung-${activityId.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  }, []);

  const handleKeyNav = useCallback((to: string) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(to);
    }
  }, [navigate]);

  if (error && !data) {
    return (
      <div>
        <div className="dash-header">
          <div>
            <h1 className="dash-greeting">{getGreeting()}, {user?.first_name}!</h1>
            <p className="text-muted">{formatDateHeader()}</p>
          </div>
        </div>
        <div className="card" style={{ padding: '40px 24px', textAlign: 'center' }}>
          <AlertTriangle size={36} style={{ color: 'var(--danger, #ef4444)', marginBottom: 12 }} />
          <p style={{ fontSize: '1rem', color: 'var(--text)', marginBottom: 16 }}>
            Daten konnten nicht geladen werden.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => loadStats()}
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <div className="dash-header">
          <div>
            <h1 className="dash-greeting">{getGreeting()}, {user?.first_name}!</h1>
            <p className="text-muted">Daten werden geladen...</p>
          </div>
        </div>
        <SkeletonDashboard />
      </div>
    );
  }

  const totalTasks =
    data.open_tasks.overdue_invoices_count +
    data.open_tasks.unread_followup_responses +
    data.open_tasks.pending_contracts;

  return (
    <div>
      {/* --- Header --- */}
      <div className="dash-header">
        <div>
          <h1 className="dash-greeting">{getGreeting()}, {user?.first_name}!</h1>
          <p className="text-muted">Hier ist Ihre Übersicht für heute.</p>
        </div>
        <span className="dash-date">{formatDateHeader()}</span>
      </div>

      {/* --- Demo Data Banner --- */}
      <DemoBanner />

      {/* --- Trial Expiry Banner --- */}
      <TrialBanner />

      {/* --- Onboarding Help Banner --- */}
      <OnboardingHelpBanner />

      {/* --- KPI Cards --- */}
      <div className="kpi-grid" data-tour="dashboard-kpis">
        <div
          className="kpi-card kpi-blue"
          role="link"
          tabIndex={0}
          aria-label="Aktive Klienten anzeigen"
          onClick={() => navigate('/klienten')}
          onKeyDown={handleKeyNav('/klienten')}
        >
          <div className="kpi-top">
            <div className="kpi-icon-box kpi-icon-blue">
              <Users size={20} />
            </div>
            <TrendBadge current={data.active_clients} previous={data.trend.clients_prev} />
          </div>
          <span className="kpi-value">{data.active_clients}</span>
          <span className="kpi-label">Aktive Klienten</span>
        </div>

        <div
          className="kpi-card kpi-green"
          role="link"
          tabIndex={0}
          aria-label="Sitzungen im Kalender anzeigen"
          onClick={() => navigate('/kalender')}
          onKeyDown={handleKeyNav('/kalender')}
        >
          <div className="kpi-top">
            <div className="kpi-icon-box kpi-icon-green">
              <CalendarDays size={20} />
            </div>
            <TrendBadge current={data.sessions_this_month} previous={data.trend.sessions_prev} />
          </div>
          <span className="kpi-value">{data.sessions_this_month}</span>
          <span className="kpi-label">Sitzungen diesen Monat</span>
        </div>

        <div
          className="kpi-card kpi-amber"
          role="link"
          tabIndex={0}
          aria-label="Offene Rechnungen anzeigen"
          onClick={() => navigate('/rechnungen')}
          onKeyDown={handleKeyNav('/rechnungen')}
        >
          <div className="kpi-top">
            <div className="kpi-icon-box kpi-icon-amber">
              <FileText size={20} />
            </div>
            <span className="kpi-sub-badge">{data.open_invoices_count} offen</span>
          </div>
          <span className="kpi-value">{formatEurShort(data.open_invoices_total_eur)}</span>
          <span className="kpi-label">Offene Rechnungen</span>
        </div>

        <div
          className="kpi-card kpi-violet"
          role="link"
          tabIndex={0}
          aria-label="Monatsumsatz in Berichten anzeigen"
          onClick={() => navigate('/berichte')}
          onKeyDown={handleKeyNav('/berichte')}
        >
          <div className="kpi-top">
            <div className="kpi-icon-box kpi-icon-violet">
              <TrendingUp size={20} />
            </div>
            <TrendBadge current={data.monthly_revenue_eur} previous={data.trend.revenue_prev} isPercent />
          </div>
          <span className="kpi-value">{formatEur(data.monthly_revenue_eur)}</span>
          <span className="kpi-label">Monatsumsatz</span>
        </div>
      </div>

      {/* --- Quick Action Pills --- */}
      <div className="quick-actions" data-tour="dashboard-actions">
        {QUICK_ACTIONS.map(({ label, icon: Icon, to }) => (
          <button
            key={to}
            className="quick-action-pill"
            onClick={() => navigate(to)}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {/* --- Top Content Grid: Chart + Tasks/Schnellzugriff --- */}
      <div className="dash-content-top">
        {/* Chart Card */}
        <div className="widget-card" data-tour="dashboard-chart">
          <div className="widget-header">
            <h3>
              <TrendingUp size={16} className="widget-header-icon" />
              Umsatz & Sitzungen
            </h3>
            <div className="chart-range-btns">
              {[3, 6, 12].map((m) => (
                <button
                  key={m}
                  className={`chart-range-btn ${chartRange === m ? 'active' : ''}`}
                  onClick={() => setChartRange(m)}
                >
                  {m}M
                </button>
              ))}
            </div>
          </div>

          {/* Chart Summary Row */}
          {chartData?.summary && (
            <div className="chart-summary-row">
              <div
                className="chart-summary-card"
                role="link"
                tabIndex={0}
                onClick={() => navigate('/berichte')}
                onKeyDown={handleKeyNav('/berichte')}
              >
                <div className="chart-summary-top">
                  <span className="chart-summary-value">{formatEur(chartData.summary.total_revenue)}</span>
                  <ChangeBadge pct={chartData.summary.revenue_change_pct} />
                </div>
                <span className="chart-summary-label">Gesamtumsatz ({chartRange}M)</span>
                <span className="chart-summary-avg">{'Ø'} {formatEurShort(chartData.summary.avg_revenue_monthly)} / Monat</span>
              </div>
              <div
                className="chart-summary-card"
                role="link"
                tabIndex={0}
                onClick={() => navigate('/berichte')}
                onKeyDown={handleKeyNav('/berichte')}
              >
                <div className="chart-summary-top">
                  <span className="chart-summary-value">{chartData.summary.total_sessions}</span>
                  <ChangeBadge pct={chartData.summary.sessions_change_pct} />
                </div>
                <span className="chart-summary-label">Sitzungen gesamt ({chartRange}M)</span>
                <span className="chart-summary-avg">{'Ø'} {chartData.summary.avg_sessions_monthly} / Monat</span>
              </div>
            </div>
          )}

          <div className="widget-body chart-body">
            {chartData && chartData.months.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={chartData.months} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CHART_GREEN} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={CHART_GREEN} stopOpacity={0.03} />
                      </linearGradient>
                      <linearGradient id="gradSessions" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CHART_BLUE} stopOpacity={0.3} />
                        <stop offset="100%" stopColor={CHART_BLUE} stopOpacity={0.03} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light, #f0ece6)" strokeOpacity={0.6} />
                    <XAxis
                      dataKey="month"
                      tickFormatter={formatChartMonth}
                      tick={{ fontSize: 12, fill: 'var(--text-muted)' }}
                      axisLine={{ stroke: 'var(--border)' }}
                    />
                    <YAxis
                      yAxisId="revenue"
                      tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={formatYAxis}
                      width={45}
                    />
                    <YAxis
                      yAxisId="sessions"
                      orientation="right"
                      tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                      hide
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      yAxisId="revenue"
                      type="monotone"
                      dataKey="revenue"
                      name="Umsatz"
                      stroke={CHART_GREEN}
                      strokeWidth={2.5}
                      fill="url(#gradRevenue)"
                      dot={{ r: 5, fill: '#fff', stroke: CHART_GREEN, strokeWidth: 2.5 }}
                      activeDot={{ r: 6, fill: '#fff', stroke: CHART_GREEN, strokeWidth: 2.5 }}
                    />
                    <Area
                      yAxisId="sessions"
                      type="monotone"
                      dataKey="sessions"
                      name="Sitzungen"
                      stroke={CHART_BLUE}
                      strokeWidth={2.5}
                      fill="url(#gradSessions)"
                      dot={{ r: 5, fill: '#fff', stroke: CHART_BLUE, strokeWidth: 2.5 }}
                      activeDot={{ r: 6, fill: '#fff', stroke: CHART_BLUE, strokeWidth: 2.5 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
                <div className="chart-legend">
                  <span className="chart-legend-item">
                    <span className="chart-legend-dot" style={{ background: CHART_GREEN }} />
                    Umsatz
                  </span>
                  <span className="chart-legend-item">
                    <span className="chart-legend-dot" style={{ background: CHART_BLUE }} />
                    Sitzungen
                  </span>
                </div>
              </>
            ) : (
              <div className="widget-empty">
                <div className="widget-empty-icon"><TrendingUp size={32} /></div>
                Noch keine Daten vorhanden
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Tasks + Schnellzugriff */}
        <div className="dash-content-top-right">
          {/* Open Tasks */}
          <div className="widget-card">
            <div className="widget-header">
              <h3>
                <AlertTriangle size={16} className="widget-header-icon" />
                Offene Aufgaben
                {totalTasks > 0 && <span className="text-muted" style={{ fontWeight: 400, fontSize: '0.82rem' }}>({totalTasks})</span>}
              </h3>
            </div>
            <div className="widget-body">
              {totalTasks === 0 ? (
                <div className="widget-empty">
                  Alles erledigt — keine offenen Aufgaben!
                </div>
              ) : (
                <>
                  {data.open_tasks.overdue_invoices_count > 0 && (
                    <div className="task-item" onClick={() => navigate('/rechnungen')} role="link" tabIndex={0} onKeyDown={handleKeyNav('/rechnungen')}>
                      <div className="task-badge danger">
                        <AlertTriangle size={16} />
                      </div>
                      <div className="task-info">
                        <div className="task-label">
                          {data.open_tasks.overdue_invoices_count} überfällige Rechnung{data.open_tasks.overdue_invoices_count > 1 ? 'en' : ''}
                        </div>
                        <div className="task-detail">
                          Gesamt: {formatEur(data.open_tasks.overdue_invoices_total_eur)}
                        </div>
                      </div>
                      <ChevronRight size={16} color="var(--text-muted)" />
                    </div>
                  )}

                  {data.open_tasks.unread_followup_responses > 0 && (
                    <div className="task-item" onClick={() => navigate('/klienten')} role="link" tabIndex={0} onKeyDown={handleKeyNav('/klienten')}>
                      <div className="task-badge warning">
                        <MessageSquare size={16} />
                      </div>
                      <div className="task-info">
                        <div className="task-label">
                          {data.open_tasks.unread_followup_responses} ungelesene Follow-Up-Antwort{data.open_tasks.unread_followup_responses > 1 ? 'en' : ''}
                        </div>
                        <div className="task-detail">Antworten im Klientenprofil auswerten</div>
                      </div>
                      <ChevronRight size={16} color="var(--text-muted)" />
                    </div>
                  )}

                  {data.open_tasks.pending_contracts > 0 && (
                    <div className="task-item" onClick={() => navigate('/klienten')} role="link" tabIndex={0} onKeyDown={handleKeyNav('/klienten')}>
                      <div className="task-badge info">
                        <FilePen size={16} />
                      </div>
                      <div className="task-info">
                        <div className="task-label">
                          {data.open_tasks.pending_contracts} {data.open_tasks.pending_contracts > 1 ? 'Vertr\u00E4ge' : 'Vertrag'} offen
                        </div>
                        <div className="task-detail">Fälle ohne unterzeichneten Vertrag</div>
                      </div>
                      <ChevronRight size={16} color="var(--text-muted)" />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Nachrichten Widget */}
          <div className="widget-card" data-tour="dashboard-messages">
            <div className="widget-header">
              <h3>
                <MessageSquare size={16} className="widget-header-icon" />
                Nachrichten
                {totalUnread > 0 && (
                  <span className="msg-unread-total">{totalUnread}</span>
                )}
              </h3>
              <a href="/nachrichten" onClick={(e) => { e.preventDefault(); navigate('/nachrichten'); }}>
                Alle anzeigen
              </a>
            </div>
            <div className="widget-body">
              {recentConvs.length === 0 ? (
                <div className="widget-empty">
                  <div className="widget-empty-icon"><MessageSquare size={32} /></div>
                  Keine Konversationen
                </div>
              ) : (
                recentConvs.map((conv) => (
                  <div
                    key={conv.id}
                    className={`dash-msg-item ${conv.unread_count > 0 ? 'dash-msg-unread' : ''}`}
                    onClick={() => navigate('/nachrichten')}
                    role="link"
                    tabIndex={0}
                    onKeyDown={handleKeyNav('/nachrichten')}
                  >
                    <div
                      className="dash-msg-avatar"
                      style={{ background: getMsgAvatarColor(conv.client_name) }}
                    >
                      {getMsgInitials(conv.client_name)}
                    </div>
                    <div className="dash-msg-content">
                      <div className="dash-msg-name">{conv.client_name || 'Unbekannt'}</div>
                      <div className="dash-msg-preview">
                        {conv.last_message_preview || 'Keine Nachrichten'}
                      </div>
                    </div>
                    <div className="dash-msg-meta">
                      <span className="dash-msg-time">{formatMsgTime(conv.last_message_at)}</span>
                      {conv.unread_count > 0 && (
                        <span className="dash-msg-badge">{conv.unread_count}</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* --- Bottom Content Grid: Sessions + Activities --- */}
      <div className="dash-content-bottom">
        {/* Upcoming Sessions */}
        <div className="widget-card">
          <div className="widget-header">
            <h3>
              <Clock size={16} className="widget-header-icon" />
              Nächste Sitzungen
            </h3>
            <a href="/kalender" onClick={(e) => { e.preventDefault(); navigate('/kalender'); }}>
              Alle anzeigen
            </a>
          </div>
          <div className="widget-body">
            {data.upcoming_sessions.length === 0 ? (
              <div className="widget-empty">
                <div className="widget-empty-icon"><CalendarDays size={32} /></div>
                Keine anstehenden Sitzungen
              </div>
            ) : (
              data.upcoming_sessions.map((s) => {
                const { day, month } = formatDay(s.scheduled_at);
                const today = isToday(s.scheduled_at);
                const durLoc = [
                  s.duration_minutes ? `${s.duration_minutes} Min` : null,
                  s.location_type ? formatLocationLabel(s.location_type) : null,
                ].filter(Boolean).join(' \u00B7 ');

                return (
                  <div
                    key={s.id}
                    className="session-row"
                    onClick={() => navigate(`/klienten/${s.client_id}`)}
                    role="link"
                    tabIndex={0}
                    onKeyDown={handleKeyNav(`/klienten/${s.client_id}`)}
                  >
                    <div className={`session-date-badge ${today ? 'session-date-today' : ''}`}>
                      <span className="day">{day}</span>
                      <span className="month">{month}</span>
                    </div>
                    <div className="session-details">
                      <div className="session-client-name">
                        {s.client_name}
                        <span className={`session-status-dot status-dot-${s.payment_status === 'keine' ? 'amber' : 'green'}`} />
                        <span className="session-type-badge">{formatSessionTypeLabel(s.session_type)}</span>
                      </div>
                      <div className="session-case-title">{s.case_title}</div>
                    </div>
                    <div className="session-meta">
                      <span className="session-time">{formatSessionTime(s.scheduled_at)}</span>
                      {durLoc && <span className="session-dur-loc">{durLoc}</span>}
                    </div>
                    <ChevronRight size={16} color="var(--text-muted)" />
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Recent Activities */}
        <div className="widget-card">
          <div className="widget-header">
            <h3>
              <Activity size={16} className="widget-header-icon" />
              Letzte Aktivitäten
            </h3>
          </div>
          <div className="widget-body activity-timeline">
            {data.recent_activities.length === 0 ? (
              <div className="widget-empty">
                <div className="widget-empty-icon"><Activity size={32} /></div>
                Noch keine Aktivitäten
              </div>
            ) : (
              data.recent_activities.map((a) => {
                const route = getActivityRoute(a);
                return (
                  <div
                    key={a.id}
                    className={`activity-item ${route ? 'activity-clickable' : ''}`}
                    onClick={route ? () => navigate(route) : undefined}
                    role={route ? 'link' : undefined}
                    tabIndex={route ? 0 : undefined}
                    onKeyDown={route ? handleKeyNav(route) : undefined}
                  >
                    <div className={`activity-dot ${getActivityDotClass(a.entity_type)}`} />
                    <div className="activity-content">
                      <div className="activity-text">
                        {a.description || `${a.action} \u2014 ${a.entity_type}`}
                      </div>
                      <div className="activity-time">{relativeTime(a.timestamp)}</div>
                    </div>
                    {a.entity_type === 'invoice' && (
                      <button
                        className="activity-pdf-btn"
                        title="PDF herunterladen"
                        onClick={(e) => handleDownloadPdf(a.id, a.entity_type, e)}
                      >
                        <Download size={14} />
                      </button>
                    )}
                    {route && <ChevronRight size={14} color="var(--text-muted)" className="activity-chevron" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
